import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setCategory } from "./ModalsReducer";

export type Group = {
  name: string;
  types: { [key: string]: string };
  ids: { [key: string]: number };
  rotations: { [key: string]: number };
  offsets: { [key: string]: { x: number; y: number; z: number } };
  scales: { [key: string]: { x: number; y: number; z: number }};
  textures: { [key: string]: string }
};

interface ConstructorState {
  movingNodes: string[];
  hoverNode: string;
  fastMove: boolean;
  types: { [key: string]: string };
  ids: { [key: string]: number };
  rotations: { [key: string]: number };
  groups: { [key: string]: Group };
  textures: { [key: string]: string }
  action: string;
  objectInfo: { [key: string]: any };
  scales: { [key: string]: { x: number; y: number; z: number } };
  saveProportions: { [key:string]: boolean };
  blockedItems: string[],
  deletedItems: {
    types: { [key: string]: string };
    ids: { [key: string]: number };
    rotations: { [key: string]: number };
    groups: { [key: string]: Group };
    scales: { [key: string]: { x: number; y: number; z: number } };
    textures: { [key: string]: string }
  }
}

let fastMove = localStorage.getItem("constructor_fastMove") === "true";

// Define the initial state using that type
const initialState: ConstructorState = {
  movingNodes: [],
  hoverNode: "",
  fastMove,
  types: {},
  ids: {},
  rotations: {},
  action: "",
  groups: {},
  textures: {},
  objectInfo: [],
  scales: {},
  saveProportions: {},
  blockedItems: [],
  deletedItems: {
    types: {},
    ids: {},
    rotations: {},
    groups: {},
    scales: {},
    textures: {}
  }
};

export const constructorSlice = createSlice({
  name: "constructor",
  initialState,
  reducers: {
    setObjectInfo: (state, action: PayloadAction<any>) => {
      state.objectInfo = action.payload;
    },
    clearGroups: (state, action: PayloadAction<{ key?: string }>) => {
      const newGroups:{ [key:string]: Group } = {};
      if(!!action.payload.key) {
        for(const key in state.groups) {
          if(key !== action.payload.key) newGroups[key] = state.groups[key];
        }
      }
      state.groups = newGroups;
    },
    deleteGroup: (state, action: PayloadAction<string>) => {
      delete state.groups[action.payload];
    },
    setGroup: (state, action: PayloadAction<{ key: string; group: Group }>) => {
      state.groups[action.payload.key] = action.payload.group;
    },
    setTexture: (state, action: PayloadAction<{ key: string, texture: string }>) => {
      state.textures[action.payload.key] = action.payload.texture;
    },
    clearTextures: (state, action: PayloadAction<{ key?: string }>) => {
      const newTextures:{ [key:string]: string } = {};
      if(!!action.payload.key) {
        for(const key in state.textures) {
          if(key !== action.payload.key) newTextures[key] = state.textures[key];
        }
      }
      state.textures = newTextures;
    },
    setAction: (state, action: PayloadAction<string>) => {
      state.action = action.payload;
    },
    toggleMovingNode: (state, action: PayloadAction<string | string[]>) => {
      let nodes: string[] = [];
      if (typeof action.payload === "string") nodes.push(action.payload);
      else nodes = action.payload;
      for (const node of nodes) {
        if (state.movingNodes.indexOf(node) !== -1) {
          state.movingNodes.splice(state.movingNodes.indexOf(node), 1);
        } else {
          state.movingNodes.push(node);
        }
      }
    },
    setMovingNode: (
      state,
      action: PayloadAction<{ index?: number; key: string|string[] }>
    ) => {
      const { index, key } = action.payload;
      if(Array.isArray(key)) {
        state.movingNodes = key;
      }
      if(index !== undefined && !Array.isArray(key)) {
        state.movingNodes[index] = key;
        setCategory(
          Array.isArray(state.types[key]) ? "group" : state.types[key].toString()
        );
      }
    },
    setHoverNode: (state, action: PayloadAction<string>) => {
      state.hoverNode = action.payload;
    },
    clearMovingNodes: (state) => {
      state.movingNodes = [];
    },
    setType: (state, action: PayloadAction<{ key: string; type: string }>) => {
      state.types[action.payload.key] = action.payload.type;
    },
    clearTypes: (state, action: PayloadAction<{ key?: string }>) => {
      const newTypes:{ [key:string]: string } = {};
      if(!!action.payload.key) {
        for(const key in state.types) {
          if(key !== action.payload.key) newTypes[key] = state.types[key];
        }
      }
      state.types = newTypes;
    },
    setId: (state, action: PayloadAction<{ key: string; id: number }>) => {
      state.ids[action.payload.key] = action.payload.id;
    },
    clearIds: (state, action: PayloadAction<{ key?: string; }>) => {
      const newIds:{ [key:string]: number } = {};
      if(!!action.payload.key) {
        for(const key in state.ids) {
          if(key !== action.payload.key) newIds[key] = state.ids[key];
        }
      }
      state.ids = newIds;
    },
    setRotation: (
      state,
      action: PayloadAction<{ key: string; angle: number }>
    ) => {
      state.rotations[action.payload.key] = action.payload.angle;
    },
    clearRotations: (state, action: PayloadAction<{ key?: string }>) => {
      const newRotations:{ [key:string]: number } = {};
      if(!!action.payload.key) {
        for(const key in state.rotations) {
          if(key !== action.payload.key) newRotations[key] = state.rotations[key];
        }
      }
      state.rotations = newRotations;
    },

    setFastMove: (state, action: PayloadAction<boolean>) => {
      localStorage.setItem("constructor_fastMove", action.payload.toString());
      state.fastMove = action.payload;
    },
    setScales: (
      state,
      action: PayloadAction<{
        key: string;
        scale: { x: number; y: number; z: number };
      }>
    ) => {
      state.scales[action.payload.key] = action.payload.scale;
    },
    clearScales: (state, action: PayloadAction<{ key?: string}>) => {
      const newScales:{ [key:string]: {x: number, y: number, z: number}} = {};
      if(!!action.payload.key) {
        for(const key in state.scales) {
          if(key !== action.payload.key) newScales[key] = state.scales[key];
        }
      }
      state.scales = newScales;
    },
    clearSaveProportions: (state, action: PayloadAction<{ key?: string }>) => {
      const newProps: { [key: string]: boolean } = {};
      if (!!action.payload.key) {
        for (const key in state.saveProportions) {
          if (key !== action.payload.key) newProps[key] = state.saveProportions[key];
        }
      }
      state.saveProportions = newProps;
    },
    setSaveProportions: (state, action: PayloadAction<{ key: string, value: boolean }>) => {
      state.saveProportions[action.payload.key] = action.payload.value;
    },
    clearBlockedItems: (state) => {
      state.blockedItems = [];
    },
    toggleBlockedItem: (state, action: PayloadAction<string>) => {
      const blocked = [...state.blockedItems];
      if(blocked.indexOf(action.payload) === -1) {
        blocked.push(action.payload);
        state.blockedItems = blocked;
      } else {
        state.blockedItems = state.blockedItems.filter((item:string) => item !== action.payload);
      }
    },

    setDeletedType: (state, action: PayloadAction<{ key: string; value: string }>) => {
      state.deletedItems.types[action.payload.key] = action.payload.value;
    },
    clearDeletedTypes: (state, action: PayloadAction<{ key?: string }>) => {
      const newTypes:{ [key:string]: string} = {};
      if(!!action.payload.key) {
        for(const key in state.deletedItems.types) {
          if(key !== action.payload.key) newTypes[key] = state.deletedItems.types[key];
        }
      }
      state.deletedItems = { ...state.deletedItems, types: newTypes };
    },
    setDeletedId: (state, action: PayloadAction<{ key: string; value: number }>) => {
      state.deletedItems.ids[action.payload.key] = action.payload.value;
    },
    clearDeletedIds: (state, action: PayloadAction<{ key?: string }>) => {
      const newIds:{ [key:string]: number} = {};
      if(!!action.payload.key) {
        for(const key in state.deletedItems.ids) {
          if(key !== action.payload.key) newIds[key] = state.deletedItems.ids[key];
        }
      }
      state.deletedItems = { ...state.deletedItems, ids: newIds };
    },
    setDeletedRotation: (state, action: PayloadAction<{ key: string; value: number }>) => {
      state.deletedItems.rotations[action.payload.key] = action.payload.value;
    },
    clearDeletedRotations: (state, action: PayloadAction<{ key?: string }>) => {
      const newRotations:{ [key:string]: number} = {};
      if(!!action.payload.key) {
        for(const key in state.deletedItems.rotations) {
          if(key !== action.payload.key) newRotations[key] = state.deletedItems.rotations[key];
        }
      }
      state.deletedItems = { ...state.deletedItems, rotations: newRotations };
    },
    setDeletedScale: (state, action: PayloadAction<{ key: string; value: { x: number; y: number; z: number } }>) => {
      state.deletedItems.scales[action.payload.key] = action.payload.value;
    },
    clearDeletedScales: (state, action: PayloadAction<{ key?: string }>) => {
      const newScales:{ [key:string]: { x: number; y: number; z: number }} = {};
      if(!!action.payload.key) {
        for(const key in state.deletedItems.scales) {
          if(key !== action.payload.key) newScales[key] = state.deletedItems.scales[key];
        }
      }
      state.deletedItems = { ...state.deletedItems, scales: newScales };
    },
    setDeletedGroup: (state, action: PayloadAction<{ key: string; value: Group }>) => {
      state.deletedItems.groups[action.payload.key] = action.payload.value;
    },
    clearDeletedGroups: (state, action: PayloadAction<{ key?: string }>) => {
      const newGroups:{ [key:string]: Group } = {};
      if(!!action.payload.key) {
        for(const key in state.deletedItems.groups) {
          if(key !== action.payload.key) newGroups[key] = state.deletedItems.groups[key];
        }
      }
      state.deletedItems = { ...state.deletedItems, groups: newGroups };
    },
    setDeletedTexture: (state, action: PayloadAction<{ key: string, texture: string }>) => {
      state.deletedItems.textures[action.payload.key] = action.payload.texture;
    },
    clearDeletedTextures: (state, action: PayloadAction<{ key?: string }>) => {
      const newTextures:{ [key:string]: string } = {};
      if(!!action.payload.key) {
        for(const key in state.deletedItems.textures) {
          if(key !== action.payload.key) newTextures[key] = state.deletedItems.textures[key];
        }
      }
      state.deletedItems.textures = newTextures;
    },
  },
});

export const {
  toggleMovingNode,
  setMovingNode,
  setHoverNode,
  clearMovingNodes,
  setFastMove,
  setType,
  clearTypes,
  setRotation,
  clearRotations,
  setId,
  clearIds,
  setAction,
  clearGroups,
  setGroup,
  deleteGroup,
  setObjectInfo,
  setScales,
  clearScales,
  setSaveProportions,
  clearSaveProportions,
  clearBlockedItems,
  toggleBlockedItem,
  setDeletedId,
  clearDeletedIds,
  setDeletedType,
  clearDeletedTypes,
  setDeletedRotation,
  clearDeletedRotations,
  setDeletedScale,
  clearDeletedScales,
  setDeletedGroup,
  clearDeletedGroups,
  setTexture,
  setDeletedTexture,
  clearTextures,
  clearDeletedTextures
} = constructorSlice.actions;

export default constructorSlice.reducer;
